import * as THREE from 'three'
import { Vec3, sub } from '../core/math/vec3'
import { Shape } from '../core/vessel/part'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { Design } from './design'
import { nodiLiberi, NodoLibero } from './attach'
import { Indicatori } from './indicators'

const COLORI: Record<string, number> = {
  command: 0xe4e7ea, tank: 0xb9c0c7, engine: 0x5b6068, decoupler: 0xd08b3a,
  aero: 0x3c424a, structural: 0x9aa2aa, parachute: 0xcfc7a8,
  heatshield: 0x5a4030, leg: 0x7b838b, rcs: 0x8b939b,
}

function geometry(shape: Shape): THREE.BufferGeometry {
  switch (shape.kind) {
    case 'cylinder': return new THREE.CylinderGeometry(shape.radius, shape.radius, shape.height, 24)
    case 'cone': return new THREE.ConeGeometry(shape.radius, shape.height, 24)
    case 'box': return new THREE.BoxGeometry(shape.x, shape.y, shape.z)
  }
}

/** Marcatore a rombo: si legge bene anche sovrapposto al modello. */
function marcatore(colore: number, raggio: number, opacita = 1): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.OctahedronGeometry(raggio, 0),
    new THREE.MeshBasicMaterial({ color: colore, transparent: opacita < 1, opacity: opacita, depthTest: false }),
  )
}

export interface EditorScene {
  readonly scene: THREE.Scene
  /** mesh delle parti, per il puntamento col mouse */
  readonly partiPuntabili: THREE.Object3D[]
  /** sfere dei nodi liberi, per il puntamento col mouse */
  readonly nodiPuntabili: THREE.Object3D[]
  ricostruisci(design: Design, indicatori: Indicatori, selezione: string | null, mostraNodi: boolean, taglia: number): void
  nodoDi(oggetto: THREE.Object3D): NodoLibero | null
  parteDi(oggetto: THREE.Object3D): string | null
}

export function createEditorScene(catalog: PartCatalog): EditorScene {
  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x0a0e14)

  // luce da studio: tre sorgenti morbide, nessuna ombra dura
  scene.add(new THREE.AmbientLight(0xffffff, 0.55))
  const chiave = new THREE.DirectionalLight(0xffffff, 1.5)
  chiave.position.set(6, 9, 7)
  scene.add(chiave)
  const riempimento = new THREE.DirectionalLight(0x9fc2e8, 0.6)
  riempimento.position.set(-8, 3, -5)
  scene.add(riempimento)

  const griglia = new THREE.GridHelper(60, 60, 0x2a3644, 0x18202b)
  scene.add(griglia)

  const modello = new THREE.Group()
  scene.add(modello)
  const gruppoNodi = new THREE.Group()
  scene.add(gruppoNodi)
  const gruppoMarker = new THREE.Group()
  scene.add(gruppoMarker)

  const partiPuntabili: THREE.Object3D[] = []
  const nodiPuntabili: THREE.Object3D[] = []
  const perOggetto = new Map<THREE.Object3D, string>()
  const nodoPerOggetto = new Map<THREE.Object3D, NodoLibero>()

  function svuota(g: THREE.Group): void {
    for (const o of [...g.children]) {
      g.remove(o)
      const m = o as THREE.Mesh
      m.geometry?.dispose()
      const mat = m.material as THREE.Material | THREE.Material[] | undefined
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose())
      else mat?.dispose()
    }
  }

  return {
    scene,
    partiPuntabili,
    nodiPuntabili,

    ricostruisci(design, indicatori, selezione, mostraNodi, taglia) {
      svuota(modello); svuota(gruppoNodi); svuota(gruppoMarker)
      partiPuntabili.length = 0
      nodiPuntabili.length = 0
      perOggetto.clear()
      nodoPerOggetto.clear()

      for (const p of design.vessel.parts) {
        const def = partDef(catalog, p.defId)
        const scelta = p.id === selezione
        const mesh = new THREE.Mesh(geometry(def.shape), new THREE.MeshStandardMaterial({
          color: scelta ? 0xffd166 : (COLORI[def.category] ?? 0x9aa2aa),
          emissive: scelta ? 0x4a3a10 : 0x000000,
          roughness: def.category === 'engine' ? 0.42 : 0.72,
          metalness: def.category === 'engine' ? 0.7 : 0.15,
          flatShading: true,
        }))
        mesh.position.set(p.position.x, p.position.y, p.position.z)
        mesh.quaternion.set(p.rotation.x, p.rotation.y, p.rotation.z, p.rotation.w)
        modello.add(mesh)
        partiPuntabili.push(mesh)
        perOggetto.set(mesh, p.id)
      }

      if (mostraNodi) {
        // grandi abbastanza da poterli centrare col mouse anche su un razzo
        // di venticinque metri inquadrato tutto intero
        const raggioNodo = Math.max(0.3, taglia * 0.022)
        for (const n of nodiLiberi(design.vessel, catalog)) {
          const sfera = new THREE.Mesh(
            new THREE.SphereGeometry(raggioNodo, 14, 12),
            new THREE.MeshBasicMaterial({ color: 0x7fd6a0, transparent: true, opacity: 0.85, depthTest: false }),
          )
          sfera.position.set(n.position.x, n.position.y, n.position.z)
          gruppoNodi.add(sfera)
          nodiPuntabili.push(sfera)
          nodoPerOggetto.set(sfera, n)
        }
      }

      // ── i marcatori: e qui che l'editor dice quello che sa.
      // Stanno DI FIANCO al razzo, non dentro: dentro sarebbero sepolti nelle
      // mesh e illeggibili proprio quando servono, cioe quando due di loro
      // sono vicini. Un filo li ricollega alla quota giusta sull'asse.
      const raggio = Math.max(0.35, taglia * 0.028)
      const lato = raggio * 2.6 + 1.2

      const metti = (p: Vec3 | null, colore: number, scala: number, offsetX: number, opacita = 1): void => {
        if (!p) return
        const m = marcatore(colore, raggio * scala, opacita)
        m.position.set(p.x + offsetX, p.y, p.z)
        m.renderOrder = 10
        gruppoMarker.add(m)

        const filo = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(p.x, p.y, p.z),
            new THREE.Vector3(p.x + offsetX, p.y, p.z),
          ]),
          new THREE.LineBasicMaterial({ color: colore, transparent: true, opacity: 0.5, depthTest: false }),
        )
        filo.renderOrder = 9
        gruppoMarker.add(filo)
      }

      metti(indicatori.com, 0xffd166, 1, -lato)             // centro di massa
      metti(indicatori.comVuoto, 0x8a6b1f, 0.72, -lato, 0.8) // a serbatoi vuoti
      metti(indicatori.cot, 0xff6b57, 0.9, lato)            // centro di spinta
      metti(indicatori.cop, 0x5fa8e8, 0.85, lato)           // centro di pressione

      // freccia della spinta: storta se la spinta e storta, ed e il punto
      if (indicatori.cot && indicatori.direzioneSpinta) {
        const d = indicatori.direzioneSpinta
        const freccia = new THREE.ArrowHelper(
          new THREE.Vector3(d.x, d.y, d.z).normalize(),
          new THREE.Vector3(indicatori.cot.x, indicatori.cot.y, indicatori.cot.z),
          Math.max(2.5, Math.min(taglia * 0.35, 2 + indicatori.spinta / 60_000)),
          0xff6b57, 1.1, 0.5,
        )
        freccia.renderOrder = 10
        gruppoMarker.add(freccia)
      }
    },

    nodoDi: (o) => nodoPerOggetto.get(o) ?? null,
    parteDi: (o) => perOggetto.get(o) ?? null,
  }
}
